import type { ElementType, ReactNode } from "react";

const tones = {
  white: "bg-white",
  mist: "bg-mist",
  ivory: "bg-ivory",
  navy: "bg-navy text-white",
  midnight: "bg-midnight text-white",
} as const;

export function Section({
  children,
  tone = "white",
  className = "",
  id,
  as: As = "section",
  ariaLabelledBy,
  compact = false,
}: {
  children: ReactNode;
  tone?: keyof typeof tones;
  className?: string;
  id?: string;
  as?: ElementType;
  ariaLabelledBy?: string;
  /** For a section that continues the one above it rather than starting a subject. */
  compact?: boolean;
}) {
  return (
    <As
      id={id}
      aria-labelledby={ariaLabelledBy}
      className={`${compact ? "section-tight" : "section"} ${tones[tone]} ${className}`}
    >
      <div className="container-page">{children}</div>
    </As>
  );
}

/**
 * Section masthead.
 *
 * Deliberately narrow (max 2 columns of text) and light on hierarchy: a small
 * label, a large serif line, and at most one supporting sentence. The lead is
 * optional and usually omitted, most sections read better without one.
 */
export function SectionHeading({
  eyebrow,
  title,
  lead,
  id,
  align = "left",
  tone = "dark",
  level = 2,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  id?: string;
  align?: "left" | "center";
  tone?: "dark" | "light";
  level?: 2 | 3;
}) {
  const Heading = (level === 2 ? "h2" : "h3") as ElementType;

  return (
    <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow ? <p className={`eyebrow ${tone === "light" ? "text-sky/80" : ""}`}>{eyebrow}</p> : null}
      <Heading
        id={id}
        className={tone === "light" ? "text-white" : ""}
        style={{ fontSize: "var(--step-h2)", marginTop: "var(--space-heading)" }}
      >
        {title}
      </Heading>
      {lead ? (
        <p
          className={tone === "light" ? "text-white/70" : "text-muted"}
          style={{ fontSize: "var(--step-lead)", marginTop: "var(--space-lead)" }}
        >
          {lead}
        </p>
      ) : null}
    </div>
  );
}
